import { Box, ErrorExclamationView, ICommand, Label, MaskedOptions, theme, themeShapeOptions, themeSizeOptions } from "Application";
import { isNullOrUndefined, isEmptyOrWhitespace } from "@shoothill/core";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { IMaskInput } from "react-imask";
import React, { useEffect, useRef, useState } from "react";
import Datetime from "react-datetime";
import moment from "moment";
import "moment/locale/en-gb";
import "react-datetime/css/react-datetime.css";
import { FieldLabel, InputWrapper } from "../CommonStyles";
import { CalendarSVG } from "Assets/Icons/CalendarSVG";
import { convertTimeToDate, isValidDateString, isValidWeekDay, getInputClassNames, getDataTestAttribute } from "./helpers";

moment.locale("en-gb");

interface IEditDateBaseProps {
    className?: string;
    command: ICommand;
    value: () => Date | undefined;
    displayName?: React.ReactNode;
    labelPosition?: "top" | "left";
    placeholder?: string;
    validationMessage?: () => string;
    style?: React.CSSProperties;
    size?: themeSizeOptions;
    shape?: themeShapeOptions;
    prefix?: JSX.Element;
    suffix?: JSX.Element;
    maxDate?: Date;
    cy?: string;
    initialDate?: Date;
    mode?: "date" | "time" | "datetime";
    dateFormat?: string;
    disableWeekends?: boolean;
    currentDate?: Date | string;
    isRequired?: boolean;
}

export const EditDate: React.FC<IEditDateBaseProps> = observer((props) => {
    const [internalString, setInternalString] = useState<string | null>(null);
    const [internalDate, setInternalDate] = useState<moment.Moment | string | undefined>(undefined);
    const [openCalendar, setOpenCalendar] = useState(false);
    const datetimeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (datetimeRef.current && !datetimeRef.current.contains(event.target as Node)) {
                setOpenCalendar(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.keyCode === 9) {
                setOpenCalendar(false);
            }
        };

        window.addEventListener("mousedown", handleOutsideClick);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("mousedown", handleOutsideClick);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        reaction(
            () => moment(props.value()).toDate(),
            (value) => {
                setInternalDate(moment(value));
                const formatted = !isNullOrUndefined(props.value()) ? moment(props.value()).format(props.dateFormat || "DD/MM/YYYY") : null;
                if (formatted) {
                    setInternalString(formatted);
                }
            },
        );

        setInternalDate(moment(props.value()));
        const formatted = !isNullOrUndefined(props.value()) ? moment(props.value()).format(props.dateFormat || "DD/MM/YYYY") : null;
        if (formatted) {
            setInternalString(formatted);
        }
    }, []);

    const handleSelectDate = (date: moment.Moment | string): void => {
        if (!props.command.canExecute()) return;

        if (typeof date === "string") {
            props.command.execute(Date.parse(date));
        } else {
            props.command.execute(date);
        }
    };

    const handleDateUpdate = (eventOrValue: any): void => {
        let newValue = eventOrValue.target?.value || eventOrValue;

        if (eventOrValue.target) {
            const convertedNewValue = convertTimeToDate(newValue);
            props.command.execute(convertedNewValue);
        }

        setInternalString(newValue);

        if (isValidDateString(newValue)) {
            const checkDate = moment(newValue.replace(/ /g, ""), props.dateFormat || "DD/MM/YYYY");
            setInternalDate(checkDate);
            props.command.execute(checkDate);
        }
    };

    const handleInputChange = (value: string, mask: any): void => {
        handleDateUpdate(mask.value);
    };

    const renderMaskedInput = (): JSX.Element => (
        <IMaskInput
            {...(MaskedOptions.Date(props.dateFormat) as any)}
            onAccept={handleInputChange}
            onFocus={() => setOpenCalendar(true)}
            className={getInputClassNames(props.className)}
            onInput={handleDateUpdate}
            placeholder={props.placeholder}
            value={internalString || ""}
            data-cy={getDataTestAttribute(typeof props.displayName === "string" ? props.displayName : undefined, props.cy)}
        />
    );

    const showLabel = props.displayName != null;
    const labelPosition = props.labelPosition || "top";
    const mode = props.mode ?? "datetime";
    const dateFormat = mode === "time" ? false : props.dateFormat || theme.dates.default;
    const timeFormat = mode !== "date";

    return (
        <InputWrapper
            style={{
                display: "flex",
                flexDirection: labelPosition === "left" ? "row" : "column",
                gap: labelPosition === "left" ? "15px" : "0",
                alignItems: labelPosition === "left" ? "center" : "stretch",
            }}
        >
            {showLabel && (
                <Box flexBox style={{ flex: labelPosition === "left" ? "0 0 auto" : "1 1 100%" }}>
                    <Label fontStyle={"label"} style={{ display: "block", marginBottom: labelPosition === "top" ? "10px" : 0 }}>
                        {props.displayName} {props.isRequired && <span style={{ color: theme.palette.common.warning }}>*</span>}
                    </Label>
                    <ErrorExclamationView haveError={!isEmptyOrWhitespace(props.validationMessage?.() as string)} validationMessage={() => props.validationMessage?.() || ""} />
                </Box>
            )}
            <Box className="inputbox" style={{ flex: labelPosition === "left" ? "1 1 auto" : "1 1 100%" }}>
                {!props.suffix && (
                    <Box ml={1} className="calender-icon" onClick={() => setOpenCalendar(true)} style={{ cursor: "pointer" }}>
                        <CalendarSVG />
                    </Box>
                )}
                <Box flexBox style={{ width: "100%", gap: 0 }}>
                    <div ref={datetimeRef}>
                        <Datetime
                            open={openCalendar}
                            renderInput={renderMaskedInput}
                            className={getInputClassNames(props.className)}
                            onChange={handleSelectDate}
                            value={internalDate}
                            locale="en-gb"
                            closeOnSelect={true}
                            dateFormat={dateFormat}
                            timeFormat={timeFormat}
                            isValidDate={(current) => isValidWeekDay(current, props.currentDate, props.disableWeekends)}
                        />
                    </div>
                </Box>
                {props.suffix && (
                    <Box className="calender-icon" display="flex" alignItems="center" mr="15px" onClick={() => setOpenCalendar(true)} style={{ cursor: "pointer" }}>
                        {props.suffix || <CalendarSVG />}
                    </Box>
                )}
            </Box>
        </InputWrapper>
    );
});
